import type {
  OffboardResponse,
  OffboardingRecordAction,
  OffboardingRecordPreview,
  UserPublic,
} from '@accounts/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { STR } from '../../../i18n';
import { ApiError, hasErrorCode } from '../../../lib/api';
import { getOffboardingPreview, offboardUser } from '../../../lib/lifecycleApi';
import { queryKeys } from '../../../lib/queryKeys';
import { Modal } from '../../../ui/Modal';
import { Callout, Chip, EmptyRow } from '../../../ui/Primitives';
import { modalButtonStyle, monoStyle } from '../../../ui/styles';

import {
  buildOffboardRequest,
  offboardingCounts,
  removeAccountLockout,
  selectedOffboardingAction,
} from './model';

const previewKey = (userId: string) => ['admin', 'access', 'offboarding', 'preview', userId] as const;

export function LoginRow({
  item,
  choice,
  onChoice,
}: {
  item: OffboardingRecordPreview;
  choice: OffboardingRecordAction;
  onChoice: (choice: OffboardingRecordAction) => void;
}) {
  const record = item.record;
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) minmax(220px,.8fr)',
        gap: 14,
        padding: '12px 0',
        borderBottom: '1px solid var(--border)',
        alignItems: 'center',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 650 }}>{record.catalogItem.name}</div>
        <div className="tx-subtle" style={{ fontSize: 11.5, marginTop: 2 }}>
          <span style={monoStyle}>{record.login ?? STR.accessCommon.noLogin}</span>
          {record.sharedWith.length === 0
            ? ` · ${STR.access.offboarding.personalLogin}`
            : ` · ${STR.access.offboarding.sharedWith(record.sharedWith.map((user) => user.name).join(', '))}`}
        </div>
        {record.sharedWith.length === 0 ? null : (
          <div style={{ fontSize: 12, marginTop: 3 }}>
            {STR.access.offboarding.passwordLabel}{' '}
            <b style={{ fontWeight: 650 }}>
              {STR.accessCommon.passwordHandling[record.passwordHandling]}
            </b>{' '}
            <span className="tx-subtle">
              ({STR.access.offboarding.passwordNote[record.passwordHandling]})
            </span>
          </div>
        )}
      </div>
      {item.choices.length === 1 ? (
        <span className="tx-subtle" style={{ fontSize: 12 }}>
          {STR.access.offboarding.action[choice]}
        </span>
      ) : (
        <select
          className="field"
          aria-label={STR.access.offboarding.choiceFor(record.catalogItem.name)}
          value={choice}
          onChange={(event) => {
            onChoice(event.target.value as OffboardingRecordAction);
          }}
        >
          {item.choices.map((option) => (
            <option key={option} value={option}>
              {STR.access.offboarding.action[option]}
            </option>
          ))}
        </select>
      )}
    </li>
  );
}

/** Step 3's red warning; nothing when no shared login is set to Remove account. */
export function RemoveAccountWarning({ logins, people }: { logins: number; people: number }) {
  if (logins === 0) return null;
  return (
    <Callout tone="danger" role="alert">
      <b>{STR.access.offboarding.removeAccountTitle(logins)}</b>{' '}
      {STR.access.offboarding.removeAccountBody(people)}
    </Callout>
  );
}

export function OffboardingDialog({
  user,
  onCancel,
  onOffboarded,
  onRefused,
}: {
  user: UserPublic;
  onCancel: () => void;
  onOffboarded: (result: OffboardResponse) => void;
  onRefused: () => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [choices, setChoices] = useState<Readonly<Record<string, OffboardingRecordAction>>>({});
  const preview = useQuery({
    queryKey: previewKey(user.id),
    queryFn: () => getOffboardingPreview(user.id),
  });
  const submit = useMutation({
    mutationFn: () => {
      if (preview.data === undefined) throw new Error('Offboarding preview is not loaded.');
      return offboardUser(user.id, buildOffboardRequest(preview.data, choices));
    },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({
        queryKey: ['admin', 'access', 'lifecycle', 'checklists', 'offboarding', user.id],
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAll });
      onOffboarded(result);
    },
    onError: (failure) => {
      if (hasErrorCode(failure, 'last_active_admin')) onRefused();
    },
  });

  const data = preview.data;
  const records = data === undefined ? [] : [...data.sharedLogins, ...data.personalLogins];
  const counts = data === undefined ? null : offboardingCounts(data, choices);
  const lockout = data === undefined ? null : removeAccountLockout(data, choices);
  const error =
    submit.error instanceof ApiError
      ? submit.error.message
      : submit.isError
        ? STR.errors.generic
        : null;
  const blocked = data?.openChecklistId !== null && data?.openChecklistId !== undefined;

  const footer = (
    <>
      <button type="button" className="ghost" style={modalButtonStyle} onClick={onCancel}>
        {STR.common.cancel}
      </button>
      {step > 1 ? (
        <button
          type="button"
          className="ghost"
          style={modalButtonStyle}
          disabled={submit.isPending}
          onClick={() => {
            setStep(step === 3 ? 2 : 1);
          }}
        >
          {STR.common.back}
        </button>
      ) : null}
      {step < 3 ? (
        <button
          type="button"
          className="prim"
          style={modalButtonStyle}
          disabled={data === undefined || blocked}
          onClick={() => {
            setStep(step === 1 ? 2 : 3);
          }}
        >
          {STR.common.continue}
        </button>
      ) : (
        <button
          type="button"
          className="dbtn"
          style={modalButtonStyle}
          disabled={submit.isPending || blocked}
          onClick={() => {
            submit.mutate();
          }}
        >
          {submit.isPending ? STR.access.offboarding.offboarding : STR.access.offboarding.confirm}
        </button>
      )}
    </>
  );

  return (
    <Modal
      title={STR.access.offboarding.title(user.name)}
      lead={STR.access.offboarding.step(step)}
      width="min(760px,100%)"
      onClose={onCancel}
      footer={footer}
    >
      <div style={{ padding: '16px 22px 20px' }}>
        {preview.isLoading ? <p className="tx-subtle">{STR.common.loading}</p> : null}
        {preview.isError ? (
          <Callout tone="danger" role="alert">
            {preview.error instanceof ApiError ? preview.error.message : STR.errors.generic}
          </Callout>
        ) : null}
        {blocked ? (
          <Callout tone="attention" role="alert">
            {STR.access.offboarding.alreadyOpen}
          </Callout>
        ) : null}
        {error === null ? null : (
          <Callout tone="danger" role="alert">
            {error}
          </Callout>
        )}

        {data !== undefined && step === 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <Callout tone="attention">{STR.access.offboarding.reviewWarning}</Callout>
            <section>
              <h3 style={{ margin: '0 0 8px', fontSize: 13.5 }}>{STR.access.offboarding.suiteApps}</h3>
              {data.suiteApps.length === 0 ? (
                <EmptyRow>{STR.access.offboarding.noSuiteApps}</EmptyRow>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {data.suiteApps.map((grant) => (
                    <li
                      key={grant.grantId}
                      style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                    >
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
                        {grant.application.name}
                      </span>
                      <Chip tone="attention" size="sm">
                        {STR.access.offboarding.willAskApp}
                      </Chip>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h3 style={{ margin: '0 0 4px', fontSize: 13.5 }}>{STR.access.offboarding.outsideLogins}</h3>
              <p className="tx-subtle" style={{ margin: 0, fontSize: 12 }}>
                {STR.access.offboarding.outsideSummary(records.length, data.sharedLogins.length)}
              </p>
            </section>
          </div>
        ) : null}

        {data !== undefined && step === 2 ? (
          <div>
            <p className="tx-subtle" style={{ margin: '0 0 8px', fontSize: 12.5 }}>
              {STR.access.offboarding.chooseLead}
            </p>
            {records.length === 0 ? (
              <EmptyRow>{STR.access.offboarding.noOutsideLogins}</EmptyRow>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {records.map((item) => (
                  <LoginRow
                    key={item.record.id}
                    item={item}
                    choice={selectedOffboardingAction(item, choices)}
                    onChoice={(choice) => {
                      setChoices((current) => ({ ...current, [item.record.id]: choice }));
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        ) : null}

        {data !== undefined && step === 3 && counts !== null && lockout !== null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <RemoveAccountWarning logins={lockout.logins} people={lockout.people} />
            <Callout tone="danger">{STR.access.offboarding.finalWarning(user.name)}</Callout>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.8 }}>
              <li>{STR.access.offboarding.signInEnds}</li>
              {counts.app_deactivation > 0 ? (
                <li>{STR.access.offboarding.appCount(counts.app_deactivation)}</li>
              ) : null}
              {(['rotate_shared_password', 'remove_vm_access', 'remove_from_shared_mailbox', 'remove'] as const).map(
                (action) =>
                  counts[action] === 0 ? null : (
                    <li key={action}>
                      {STR.access.offboarding.actionCount(counts[action], STR.access.offboarding.action[action])}
                    </li>
                  ),
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
